import { Component, Input } from '@angular/core';
import {
    compareDates,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LotamFull,
    NoticeMissionAcknowledgementDto,
    NoticeMissionDto,
    NoticesService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { TableColumn, TableConfig } from '@flyfreely-portal-ui/flyfreely-table';
import {
    FormatDateTimePipe,
    FormatDurationPipe,
    FormatPersonPipe
} from '@flyfreely-portal-ui/ui';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';

interface MissionRow {
    missionName: string;
    /** Raw ISO mission date, used for chronological sorting. */
    missionDate: string;
    /** Formatted mission date, used for display and searching. */
    missionDateDisplay: string;
    /** Raw estimated time in seconds, used for sorting. */
    duration: number;
    /** Formatted duration, used for display and searching. */
    durationDisplay: string;
    contactInformation: string;
    acknowledgements: NoticeMissionAcknowledgementDto[];
    organisationName: string;
}

@Component({
    selector: 'notice-missions-dialogue',
    templateUrl: './notice-missions-dialogue.component.html'
})
export class NoticeMissionsDialogueComponent {
    @Input() notice: LotamFull;
    @Input() managingOrganisationId: number;
    @Input() timeZone?: string;

    rows: MissionRow[] = [];
    availableColumns: TableColumn<MissionRow>[];
    tableConfig: TableConfig<MissionRow>;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private noticesService: NoticesService,
        private modal: BsModalRef<NoticeMissionsDialogueComponent>,
        private logging: FlyFreelyLoggingService,
        private formatDateTime: FormatDateTimePipe,
        private formatDuration: FormatDurationPipe,
        private formatPerson: FormatPersonPipe
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.availableColumns = [
            {
                name: 'Mission Name',
                value: 'missionName',
                searchable: 'text',
                selectable: false,
                defaultSelection: true,
                sortable: true
            },
            {
                name: 'Organisation Name',
                value: 'organisationName',
                searchable: 'text',
                selectable: false,
                defaultSelection: true,
                sortable: true
            },
            {
                name: 'Start Date',
                key: 'missionDate',
                value: 'missionDateDisplay',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: true,
                compareFunction: (a, b) => compareDates(a.rawData, b.rawData)
            },
            {
                name: 'Duration',
                key: 'duration',
                value: 'durationDisplay',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: true,
                compareFunction: (a, b) => (a.rawData ?? 0) - (b.rawData ?? 0)
            },
            {
                name: 'Contact Information',
                value: 'contactInformation',
                searchable: 'text',
                selectable: false,
                defaultSelection: true,
                sortable: true
            },
            {
                name: 'Acknowledged By',
                key: 'acknowledgedBy',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: false
            }
        ];

        this.tableConfig = {
            limit: null,
            actions: [],
            timeZone: this.timeZone
        };

        this.refresh();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refresh() {
        if (this.notice?.id == null || this.managingOrganisationId == null) {
            return;
        }
        this.noticesService
            .findNoticeMissions(this.notice.id, this.managingOrganisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missions =>
                    (this.rows = missions.map(m => this.toRow(m))),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while loading missions affected by this LOTAM: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    private toRow(noticeMission: NoticeMissionDto): MissionRow {
        const mission = noticeMission.mission;
        const contactInformation = [
            mission.pointOfContact,
            mission.pointOfContactInformation
        ]
            .filter(part => part != null && part !== '')
            .join(' - ');
        return {
            missionName: mission.name,
            missionDate: mission.missionDate,
            missionDateDisplay:
                this.formatDateTime.transform(
                    mission.missionDate,
                    this.timeZone
                ) ?? '',
            duration: mission.missionEstimatedTime,
            durationDisplay:
                this.formatDuration.transform(mission.missionEstimatedTime) ??
                '',
            contactInformation,
            acknowledgements: noticeMission.acknowledgements ?? [],
            organisationName: mission.organisationName
        };
    }

    formatAcknowledgements(
        acknowledgements: NoticeMissionAcknowledgementDto[]
    ): string {
        if (acknowledgements == null || acknowledgements.length === 0) {
            return '';
        }
        if (acknowledgements.length === 1) {
            return `${this.formatPerson.transform(
                acknowledgements[0].person
            )} - ${this.formatDateTime.transform(
                acknowledgements[0].acknowledgementTime
            )}`;
        }
        const formattedAcknowledgements = acknowledgements.map(
            a =>
                `${this.formatPerson.transform(
                    a.person
                )} - ${this.formatDateTime.transform(a.acknowledgementTime)}`
        );
        let acknowledgementString: string = '';
        formattedAcknowledgements.forEach((acknowledgement, i) => {
            acknowledgementString =
                acknowledgementString.concat(acknowledgement);
            if (i === formattedAcknowledgements.length - 1) {
                return;
            } else if (i === formattedAcknowledgements.length - 2) {
                acknowledgementString = acknowledgementString.concat(' & ');
                acknowledgementString = acknowledgementString.concat('<br>');
            } else {
                acknowledgementString = acknowledgementString.concat(', ');
                acknowledgementString = acknowledgementString.concat('<br>');
            }
        });
        return acknowledgementString;
    }

    onClose() {
        this.modal.hide();
    }
}
